<template>
  <v-container>
    <v-row>
      <v-col cols="12" align="center" justify="center">
        <v-card elevation="2">
          <v-card-title>Subscriptions</v-card-title>
          <div v-if="loading">
            <v-row>
              <v-col cols="11" align="center" justify="center">
                <v-row align="center" justify="center">
                  <v-text-field color="success" loading disabled></v-text-field>
                </v-row>
              </v-col>
            </v-row>
          </div>
          <div>
            <v-simple-table :loading="loading">
              <thead>
                <tr>
                  <th></th>
                  <th class="text-left">Product</th>
                  <th class="text-left">Start Date</th>
                  <th class="text-left">End Date</th>
                  <th class="text-left">Months</th>
                  <th class="text-left">Active</th>
                  <th class="text-left">Payment Gateway</th>
                  <th class="text-left">Transition to</th>
                  <th class="text-left">Surveys</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="subscription in subscriptions" :key="subscription.id">
                  <td align="center" class="py-2">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          v-bind="attrs"
                          v-on="on"
                          :to="'/client-profile/' + userId + '/manage/subscription/' + subscription.id"
                        >
                          <v-icon color="primary">create</v-icon>
                        </v-btn>
                      </template>
                      <span>Edit subscription</span>
                    </v-tooltip>
                  </td>
                  <td>{{ subscription.product.name }}</td>
                  <td>{{ formatDate(subscription.startDate) }}</td>
                  <td>
                    {{ subscription.endDate ? formatDate(subscription.endDate) : "-" }}
                  </td>

                  <td>
                    {{ computeMonths(subscription.startDate, subscription.endDate) }}
                  </td>

                  <td>
                    <div></div>
                    <v-icon
                      v-if="subscription.active"
                      :color="!subscription.endDate ? 'success' : 'warning'"
                      style="display: block"
                      >check_circle</v-icon
                    >
                    <v-icon v-else color="error" style="display: block">cancel</v-icon>
                  </td>

                  <td>
                    {{ subscription.billingOrder ? "Peach Payments" : "Payfast" }}
                  </td>
                  <td>
                    {{ transitionProduct(subscription) }}
                  </td>
                  <td v-html="getSurveys(subscription)"></td>
                </tr>
              </tbody>
            </v-simple-table>
          </div>
        </v-card>
        <v-dialog v-model="subscriptionModal" width="900">
          <v-card>
            <v-toolbar dark color="primary">
              <v-btn icon dark @click="close()">
                <v-icon>mdi-close</v-icon>
              </v-btn>
              <v-spacer></v-spacer>
            </v-toolbar>
            <v-card-text>
              <v-row>
                <v-col cols="9">
                  <h2 class="ma-3">
                    Please set the date when the client will be finishing the program
                  </h2>
                  <v-row>
                    <v-col cols="12">
                      <validation-observer ref="observer" v-slot="{ invalid }">
                        <v-form>
                          <v-row>
                            <v-col cols="6">
                              <v-menu
                                v-model="menu"
                                :close-on-content-click="false"
                                :nudge-right="40"
                                transition="scale-transition"
                                offset-y
                                min-width="290px"
                              >
                                <template v-slot:activator="{ on, attrs }">
                                  <ValidationProvider
                                    mode="lazy"
                                    name="Date"
                                    rules="required"
                                    v-slot="{ errors, valid }"
                                  >
                                    <v-text-field
                                      v-model="form.requiredDate"
                                      label="Finish date"
                                      prepend-icon="mdi-calendar"
                                      readonly
                                      v-bind="attrs"
                                      v-on="on"
                                      :error-messages="errors"
                                      :success="valid"
                                    ></v-text-field>
                                  </ValidationProvider>
                                </template>
                                <v-date-picker v-model="form.requiredDate" @input="menu = false"></v-date-picker>
                              </v-menu>
                            </v-col>
                            <v-col cols="6">
                              <ValidationProvider name="Time" rules="required" v-slot="{ errors, valid }">
                                <v-text-field
                                  v-model="form.requiredTime"
                                  type="time"
                                  label="Time of day"
                                  :error-messages="errors"
                                  :success="valid"
                                ></v-text-field>
                              </ValidationProvider>
                            </v-col>
                          </v-row>
                        </v-form>
                        <v-btn
                          class="float-left"
                          v-if="selectedSubscription.endDate"
                          @click="removeEndDate"
                          :loading="saving"
                          >Remove end date</v-btn
                        >

                        <v-btn class="float-right" @click="setEndDate" :disabled="invalid" :loading="saving"
                          >Set end date of subscription</v-btn
                        >
                      </validation-observer>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-dialog>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { SUBSCRIPTIONS_QUERY, UPDATE_SUBSCRIPTION_MUTATION } from "../../../constants/graphql/users/subscription.js";
import { YYYYMMDD_readable } from "../../../lib/fancy_date";
import { extractNodes } from "@/lib/array";
import { decodeId } from "@/lib/string";

export default {
  name: "ClientSubscription",
  props: ["userId"],
  data() {
    return {
      loading: true,
      saving: false,
      subscriptions: null,
      subscriptionModal: false,
      menu: false,
      selectedSubscription: null,
      form: {
        requiredDate: null,
        requiredTime: null,
      },
    };
  },
  methods: {
    getSurveys(subscription) {
      const subscriptionSurveys = extractNodes(subscription?.subscriptionsurveySet?.edges);
      const surveyLinks = subscriptionSurveys?.map((subscriptionSurvey) => {
        return `<a target="_blank" href="/reporting/survey-results/${decodeId(subscriptionSurvey.id)}">${
          subscriptionSurvey.survey.surveyType
        }</a>`;
      });
      return surveyLinks.join(", ");
    },
    transitionProduct(subscription) {
      if (subscription.transitionProductVariant) {
        return `${subscription.transitionProductVariant.product.title} - ${subscription.transitionProductVariant.title}`;
      }
      return "";
    },
    formatDate(date) {
      return YYYYMMDD_readable(date);
    },
    computeMonths(startDate, endDate) {
      if (!endDate) {
        return "-";
      }
      const sd = this.$moment(startDate);
      const ed = this.$moment(endDate);
      const duration = this.$moment.duration(ed.diff(sd));
      if (isNaN(duration)) {
        return "-";
      }
      return duration.asMonths().toFixed(2);
    },
    handleModal(subscription) {
      this.selectedSubscription = subscription;
      if (this.selectedSubscription.endDate) {
        this.form.requiredDate = this.$moment(this.selectedSubscription.endDate).format("YYYY-MM-DD");
        this.form.requiredTime = this.$moment(this.selectedSubscription.endDate).format("hh:mm");
      }
      this.subscriptionModal = true;
    },
    close() {
      this.selectedSubscription = null;
      this.subscriptionModal = false;
    },
    removeEndDate() {
      this.form.requiredDate = null;
      this.form.requiredTime = null;
      this.setEndDate(false);
    },
    setEndDate(setDate = true) {
      this.saving = true;
      let requiredDate = "";

      if (setDate) {
        requiredDate = new Date(this.form.requiredDate + " " + this.form.requiredTime);
      }

      this.$apollo
        .mutate({
          mutation: UPDATE_SUBSCRIPTION_MUTATION,
          variables: {
            subscriptionId: this.selectedSubscription.id,
            endDate: requiredDate,
          },
          refetchQueries: ["subscriptionsQuery"],
        })
        .then((result) => {
          if (!result.data.updateSubscriptionRecord.updated) {
            alert(result.data.updateSubscriptionRecord.error);
          } else {
            let toastMsg = "Client will be finishing -" + requiredDate;
            if (!setDate) {
              toastMsg = "End date was removed from subscription";
            }
            this.$toasted.success(toastMsg, {
              icon: "arm-flex",
              duration: 2000,
              position: "bottom-center",
            });
          }
          this.saving = false;
          this.$refs.observer.reset();
        })
        .catch((error) => {
          this.saving = false;
        });
    },
  },
  apollo: {
    subscriptions: {
      query: SUBSCRIPTIONS_QUERY,
      variables() {
        return {
          userId: this.userId,
          surveySubmittedDate_Lte: this.$moment().format("YYYY-MM-DD"),
        };
      },
      fetchPolicy: "cache-and-network",
      result(response) {
        if (response.data) {
          this.loading = false;
        }
      },
    },
  },
};
</script>
